import {
  GraphQLObjectType,
  GraphQLString,
  GraphQLNonNull,
  GraphQLList,
  GraphQLInt,
  GraphQLEnumType
} from 'graphql'
import { dateTimeType, DateTime } from './date-time-type'

import {
  vulnerabilityApplicationType,
  sessionType,
  vulnerabilityType,
  GQLVulnerabilityApplication,
  GQLVulnerability,
  Session
} from './vulnerability'

import { SinkGroupSeverityName } from '../../../components/vulnerability-severity/sink-group-severity'
import { SinkGroupStatusName } from '../../../components/vulnerability-status/sink-group-status'

export interface GQLSinkGroup {
  readonly application: GQLVulnerabilityApplication | null
  readonly count: number
  readonly title: string
  readonly firstDetected: DateTime
  readonly lastDetected: DateTime
  readonly ruleName: string
  readonly severity: SinkGroupSeverityName
  readonly sessions: Session[]
  readonly sink: string
  readonly sinkHash: string
  readonly status: SinkGroupStatusName
  readonly substatus: string | null
  readonly protectStatus: string | null
}

// GraphQL resolves missing objects as null therefore it is best to type it as null instead of undefined
export interface GQLVulnerabilityOrSinkGroup {
  readonly id: string
  readonly vulnerability: GQLVulnerability | null
  readonly sinkGroup: GQLSinkGroup | null
}

export const sinkGroupSeverityType = new GraphQLEnumType({
  name: 'VulnerabilitySinkGroupSeverity',
  values: {
    NOTE: {
      value: 'NOTE'
    },
    LOW: {
      value: 'LOW'
    },
    MEDIUM: {
      value: 'MEDIUM'
    },
    HIGH: {
      value: 'HIGH'
    },
    CRITICAL: {
      value: 'CRITICAL'
    },
    MULTIPLE: {
      value: 'MULTIPLE'
    }
  }
})

export const sinkGroupStatusType = new GraphQLEnumType({
  name: 'VulnerabilitySinkGroupStatus',
  values: {
    Reported: {
      value: 'Reported'
    },
    Suspicious: {
      value: 'Suspicious'
    },
    Confirmed: {
      value: 'Confirmed'
    },
    NotAProblem: {
      value: 'NotAProblem'
    },
    Remediated: {
      value: 'Remediated'
    },
    Fixed: {
      value: 'Fixed'
    },
    AutoRemediated: {
      value: 'AutoRemediated'
    },
    Multiple: {
      value: 'Multiple'
    }
  }
})

export const vulnerabilitySinkGroupType = new GraphQLObjectType({
  name: 'VulnerabilitySinkGroup',
  fields: () => ({
    application: {
      description: 'The application that contains the sink group',
      type: vulnerabilityApplicationType
    },
    count: {
      description: 'The number of vulnerabilities in the sink group',
      type: new GraphQLNonNull(GraphQLInt)
    },
    lastDetected: {
      description:
        'Timestamp from the last vulnerability detected associated to the sink group',
      type: new GraphQLNonNull(dateTimeType)
    },
    firstDetected: {
      description:
        'Timestamp from the earliest vulnerability detected associated to the sink group',
      type: new GraphQLNonNull(dateTimeType)
    },
    ruleName: {
      description: 'Vulnerability rule name',
      type: new GraphQLNonNull(GraphQLString)
    },
    status: {
      description:
        'Translated status value when all vulns have the same status. "Multiple" when vulns have more than one status',
      type: new GraphQLNonNull(GraphQLString)
    },
    substatus: {
      description:
        'Translated sub-status value when all vulns have the same sub-status. "Multiple" when vulns have more than one sub-status',
      type: GraphQLString
    },
    severity: {
      description:
        'Translated severity value when all vulns have the same severity. "Multiple" when vulns have more than one severity',
      type: new GraphQLNonNull(sinkGroupSeverityType)
    },
    title: {
      description: 'Translated sink group title',
      type: new GraphQLNonNull(GraphQLString)
    },
    sessions: {
      description:
        'Combined session metadata associated to all vulnerabilities in the sink group',
      type: new GraphQLList(new GraphQLNonNull(sessionType))
    },
    sink: {
      description: 'Vulnerability sink value',
      type: new GraphQLNonNull(GraphQLString)
    },
    sinkHash: {
      description: 'Hash of vulnerability sink value',
      type: new GraphQLNonNull(GraphQLString)
    },
    protectStatus: {
      description: 'Status of vuln within protect',
      type: GraphQLString
    }
  })
})

export const vulnerabilityOrSinkGroupType = new GraphQLObjectType({
  name: 'VulnerabilityOrSinkGroup',
  fields: () => ({
    id: {
      description:
        'A randomly generated ID used to find items from apollo cache',
      type: new GraphQLNonNull(GraphQLString)
    },
    sinkGroup: {
      description: 'Vulnerability sink group',
      type: vulnerabilitySinkGroupType
    },
    vulnerability: {
      description: 'Vulnerability',
      type: vulnerabilityType
    }
  })
})
